import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Clock, Calendar, Check, AlertCircle, X, Crown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };

const ScheduleTab = ({ profile, showMsg }) => {
  const [slots, setSlots] = useState(() => {
    const existing = profile.availability?.slots || [];
    return DAYS.map(day => {
      const found = existing.find(s => s.day === day);
      return found || { day, start: '09:00', end: '17:00', enabled: false };
    });
  });
  const [duration, setDuration] = useState(profile.availability?.meeting_duration || 30);
  const [tz, setTz] = useState(profile.availability?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [bookingMsg, setBookingMsg] = useState(profile.availability?.booking_message || '');
  const [saving, setSaving] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [notifPrefs, setNotifPrefs] = useState(profile.notification_prefs || { new_contact: true, new_booking: true, booking_cancelled: true });
  const [savingPrefs, setSavingPrefs] = useState(false);
  
  // Google Calendar state
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, is_pro: false });
  const [loadingCalendar, setLoadingCalendar] = useState(true);
  const [connectingCalendar, setConnectingCalendar] = useState(false);

  useEffect(() => {
    axios.get(`${API}/contacts/bookings/list`).then(r => setBookings(r.data.bookings)).catch(() => {}).finally(() => setLoadingBookings(false));
    
    // Check calendar connection status
    axios.get(`${API}/calendar/status`).then(r => setCalendarStatus(r.data)).catch(() => {}).finally(() => setLoadingCalendar(false));
    
    // Check for calendar connection result in URL params
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected') === 'true') {
      showMsg('Google Calendar connected successfully!');
      setCalendarStatus(prev => ({ ...prev, connected: true }));
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('calendar_error')) {
      showMsg('Failed to connect Google Calendar. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [showMsg]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile/availability`, { slots: slots.filter(s => s.enabled), meeting_duration: duration, timezone: tz, booking_message: bookingMsg });
      showMsg('Availability saved');
    } catch (err) { showMsg(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const updateBooking = async (id, status) => {
    try {
      await axios.patch(`${API}/contacts/bookings/${id}`, { status });
      setBookings(bookings.map(b => b.id === id ? { ...b, status } : b));
    } catch {}
  };

  const connectGoogleCalendar = async () => {
    setConnectingCalendar(true);
    try {
      const res = await axios.get(`${API}/calendar/connect`);
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Failed to connect Google Calendar');
      setConnectingCalendar(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await axios.post(`${API}/calendar/disconnect`);
      setCalendarStatus(prev => ({ ...prev, connected: false }));
      showMsg('Google Calendar disconnected');
    } catch {
      showMsg('Failed to disconnect');
    }
  };

  const updateSlot = (day, field, value) => setSlots(slots.map(s => s.day === day ? { ...s, [field]: value } : s));

  return (
    <div data-testid="schedule-tab">
      <h2 className="font-heading text-2xl font-normal text-gray-900 mb-2">Appointment Scheduling</h2>
      <p className="text-sm text-gray-500 mb-8">Let visitors book time with you directly from your card</p>

      {/* Google Calendar Integration - Pro Feature */}
      <div className="mb-8 p-5 rounded-xl border border-gray-100 bg-gray-50/50" data-testid="google-calendar-section">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${calendarStatus.connected ? 'bg-green-100' : 'bg-gray-100'}`}>
              <Calendar className={`w-5 h-5 ${calendarStatus.connected ? 'text-green-600' : 'text-gray-500'}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">Google Calendar Sync</h3>
                <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                  <Crown className="w-3 h-3" /> Pro
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {calendarStatus.connected 
                  ? 'Connected - your calendar events will block booking slots'
                  : 'Automatically block times from your Google Calendar'
                }
              </p>
            </div>
          </div>
          
          {!loadingCalendar && (
            <div>
              {!calendarStatus.is_pro ? (
                <button
                  disabled
                  className="text-xs font-semibold text-gray-400 bg-gray-100 px-4 py-2 rounded-lg cursor-not-allowed"
                  title="Upgrade to Pro to use this feature"
                >
                  Upgrade to Pro
                </button>
              ) : calendarStatus.connected ? (
                <button
                  onClick={disconnectGoogleCalendar}
                  className="flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg transition-colors"
                  data-testid="disconnect-calendar-btn"
                >
                  <X className="w-3 h-3" /> Disconnect
                </button>
              ) : (
                <button
                  onClick={connectGoogleCalendar}
                  disabled={connectingCalendar}
                  className="flex items-center gap-2 text-xs font-semibold text-white bg-gray-900 hover:bg-black px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  data-testid="connect-calendar-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {connectingCalendar ? 'Connecting...' : 'Connect Google Calendar'}
                </button>
              )}
            </div>
          )}
        </div>
        
        {calendarStatus.connected && (
          <div className="mt-4 flex items-center gap-2 text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg">
            <Check className="w-4 h-4" />
            <span>Calendar events will automatically block booking slots. Meeting invites will be sent to both you and your visitors.</span>
          </div>
        )}
        
        {!calendarStatus.is_pro && !loadingCalendar && (
          <div className="mt-4 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            <span>Upgrade to Pro to sync with Google Calendar and prevent double bookings.</span>
          </div>
        )}
      </div>

      <div className="mb-8">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Availability</h3>
        <div className="space-y-2">
          {slots.map(slot => (
            <div key={slot.day} className="flex items-center gap-3 py-2" data-testid={`schedule-${slot.day}`}>
              <button onClick={() => updateSlot(slot.day, 'enabled', !slot.enabled)}
                className={`w-8 h-5 rounded-full transition-colors relative ${slot.enabled ? 'bg-indigo-600' : 'bg-gray-200'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${slot.enabled ? 'left-3.5' : 'left-0.5'}`} />
              </button>
              <span className={`w-24 text-sm font-medium ${slot.enabled ? 'text-gray-900' : 'text-gray-400'}`}>{DAY_LABELS[slot.day]}</span>
              {slot.enabled && (
                <div className="flex items-center gap-2">
                  <input type="time" value={slot.start} onChange={e => updateSlot(slot.day, 'start', e.target.value)}
                    className="bg-white border border-gray-200 rounded-lg text-gray-900 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                  <span className="text-gray-400 text-xs">to</span>
                  <input type="time" value={slot.end} onChange={e => updateSlot(slot.day, 'end', e.target.value)}
                    className="bg-white border border-gray-200 rounded-lg text-gray-900 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Duration</label>
          <select value={duration} onChange={e => setDuration(Number(e.target.value))}
            className="w-full bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100" data-testid="meeting-duration">
            <option value={15}>15 min</option><option value={30}>30 min</option><option value={45}>45 min</option><option value={60}>60 min</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Timezone</label>
          <input value={tz} onChange={e => setTz(e.target.value)}
            className="w-full bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100" data-testid="timezone-input" />
        </div>
      </div>

      <div className="mb-6">
        <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Confirmation Message</label>
        <input value={bookingMsg} onChange={e => setBookingMsg(e.target.value)} placeholder="Looking forward to meeting you!"
          className="w-full bg-white border border-gray-200 rounded-lg text-gray-900 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100" data-testid="booking-message" />
      </div>

      <button onClick={save} disabled={saving}
        className="bg-gray-900 text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-black transition-colors disabled:opacity-50 flex items-center gap-2 mb-10"
        data-testid="save-availability-btn">
        <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Availability'}
      </button>

      {/* Bookings List */}
      <div className="border-t border-gray-100 pt-8">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Upcoming Bookings</h3>
        {loadingBookings ? <p className="text-sm text-gray-400">Loading...</p>
        : bookings.length === 0 ? <p className="text-sm text-gray-400">No bookings yet</p>
        : (
          <div className="space-y-2" data-testid="bookings-list">
            {bookings.filter(b => b.status !== 'cancelled').map(b => (
              <div key={b.id} className="bg-white border border-gray-100 rounded-xl p-4 flex items-center justify-between" data-testid={`booking-${b.id}`}>
                <div>
                  <p className="font-semibold text-sm text-gray-900">{b.visitor_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    <Clock className="w-3 h-3 inline mr-1" />{b.date} at {b.time}
                    {b.visitor_email && ` · ${b.visitor_email}`}
                  </p>
                  {b.note && <p className="text-[10px] text-gray-400 mt-1">{b.note}</p>}
                </div>
                <button onClick={() => updateBooking(b.id, 'cancelled')}
                  className="text-xs font-semibold text-gray-400 hover:text-red-500 transition-colors px-3 py-1.5 rounded-lg hover:bg-red-50"
                  data-testid={`cancel-booking-${b.id}`}>Cancel</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification Preferences */}
      <div className="border-t border-gray-100 pt-8">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Email Notifications</h3>
        <div className="space-y-3 mb-4">
          {[
            { key: 'new_contact', label: 'New contact submission' },
            { key: 'new_booking', label: 'New appointment booking' },
            { key: 'booking_cancelled', label: 'Booking cancellation' },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between py-1" data-testid={`notif-pref-${item.key}`}>
              <span className="text-sm text-gray-700">{item.label}</span>
              <button onClick={() => setNotifPrefs(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                className={`w-9 h-5 rounded-full transition-colors relative ${notifPrefs[item.key] ? 'bg-indigo-600' : 'bg-gray-200'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${notifPrefs[item.key] ? 'left-4' : 'left-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
        <button onClick={async () => { setSavingPrefs(true); try { await axios.put(`${API}/profile/notification-prefs`, notifPrefs); showMsg('Preferences saved'); } catch {} finally { setSavingPrefs(false); } }}
          disabled={savingPrefs}
          className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
          data-testid="save-notif-prefs-btn">
          {savingPrefs ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
};

export default ScheduleTab;
